// 数据合并服务
// 智能合并本地和云端数据，采用时间戳优先的冲突解决策略

import type { AppData, Workspace } from '@ext-bookmark/shared/types';
import type { Todo } from '@ext-bookmark/shared/types';

/**
 * 合并两个 AppData 对象
 * 采用时间戳优先策略：保留更新时间最新的数据
 */
export function mergeAppData(local: AppData, remote: AppData): AppData {
  // 合并工作区
  const mergedWorkspaces = mergeWorkspaces(
    local.workspaces || [],
    remote.workspaces || []
  );

  // 合并待办事项
  const mergedTodos = mergeTodos(
    local.todos || [],
    remote.todos || []
  );

  // 确定当前工作区（优先使用本地选择）
  const currentWorkspaceId = local.currentWorkspaceId || remote.currentWorkspaceId;
  const currentComponentId = local.currentComponentId || remote.currentComponentId;

  // 合并主题配置（优先使用本地）
  const theme = local.theme || remote.theme || 'light';
  const themeConfig = local.themeConfig || remote.themeConfig;

  // 使用最新的更新时间
  const localUpdatedAt = getAppDataUpdatedAt(local);
  const remoteUpdatedAt = getAppDataUpdatedAt(remote);
  const _mergedUpdatedAt = Math.max(localUpdatedAt, remoteUpdatedAt);
  void _mergedUpdatedAt;

  return {
    workspaces: mergedWorkspaces,
    currentWorkspaceId,
    currentComponentId,
    theme,
    themeConfig,
    todos: mergedTodos,
  };
}

/**
 * 合并工作区列表
 */
function mergeWorkspaces(local: Workspace[], remote: Workspace[]): Workspace[] {
  const workspaceMap = new Map<string, Workspace>();

  // 先添加远程工作区
  remote.forEach(workspace => {
    workspaceMap.set(workspace.id, { ...workspace });
  });

  // 合并本地工作区
  local.forEach(localWorkspace => {
    const remoteWorkspace = workspaceMap.get(localWorkspace.id);

    if (!remoteWorkspace) {
      // 本地有但远程没有，添加本地工作区
      workspaceMap.set(localWorkspace.id, { ...localWorkspace });
    } else {
      // 两者都有，使用更新时间最新的
      if (localWorkspace.updatedAt > remoteWorkspace.updatedAt) {
        workspaceMap.set(localWorkspace.id, { ...localWorkspace });
      } else {
        // 远程更新，但需要合并组件（字段级别合并）
        workspaceMap.set(localWorkspace.id, mergeWorkspace(remoteWorkspace, localWorkspace));
      }
    }
  });

  // 返回合并后的工作区列表（按更新时间排序）
  return Array.from(workspaceMap.values()).sort((a, b) => {
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });
}

/**
 * 合并单个工作区（字段级别合并）
 */
function mergeWorkspace(primary: Workspace, secondary: Workspace): Workspace {
  // 如果主工作区更新时间更新，直接返回主工作区
  if (primary.updatedAt > secondary.updatedAt) {
    return { ...primary };
  }

  // 否则合并字段
  return {
    ...primary,
    // 保留主工作区的名称（如果远程更新）
    name: primary.name,
    // 合并组件（如果存在）
    components: mergeComponents(
      primary.components || [],
      secondary.components || []
    ),
    // 合并图标配置（优先使用主工作区的）
    icon: primary.icon || secondary.icon,
    // 使用最新的更新时间
    updatedAt: Math.max(primary.updatedAt || 0, secondary.updatedAt || 0),
    createdAt: Math.min(primary.createdAt || 0, secondary.createdAt || 0),
  };
}

/**
 * 合并组件列表
 */
function mergeComponents(
  primary: Workspace['components'] = [],
  secondary: Workspace['components'] = []
): Workspace['components'] {
  const componentMap = new Map<string, typeof primary[0]>();

  // 先添加主组件
  primary.forEach(component => {
    componentMap.set(component.id, { ...component });
  });

  // 合并次组件
  secondary.forEach(component => {
    const existing = componentMap.get(component.id);
    if (!existing) {
      // 次组件有但主组件没有，添加
      componentMap.set(component.id, { ...component });
    } else {
      // 两者都有，使用更新时间最新的
      if (component.updatedAt > existing.updatedAt) {
        componentMap.set(component.id, { ...component });
      }
    }
  });

  return Array.from(componentMap.values());
}

/**
 * 合并待办事项列表（按 updatedAt 取更新者）
 */
export function mergeTodos(local: Todo[], remote: Todo[]): Todo[] {
  const todoMap = new Map<string, Todo>();

  // 先添加远程待办事项
  remote.forEach(todo => {
    todoMap.set(todo.id, { ...todo });
  });

  // 合并本地待办事项
  local.forEach(localTodo => {
    const remoteTodo = todoMap.get(localTodo.id);

    if (!remoteTodo) {
      // 本地有但远程没有，添加本地待办事项
      todoMap.set(localTodo.id, { ...localTodo });
    } else {
      // 两者都有，使用更新时间最新的
      const localUpdatedAt = localTodo.updatedAt || localTodo.createdAt || 0;
      const remoteUpdatedAt = remoteTodo.updatedAt || remoteTodo.createdAt || 0;

      if (localUpdatedAt > remoteUpdatedAt) {
        todoMap.set(localTodo.id, { ...localTodo });
      } else {
        todoMap.set(localTodo.id, { ...remoteTodo });
      }
    }
  });

  // 返回合并后的待办事项列表
  return Array.from(todoMap.values());
}

/**
 * 获取 AppData 的更新时间
 * 取所有工作区和待办事项中的最大更新时间
 */
function getAppDataUpdatedAt(data: AppData): number {
  let maxUpdatedAt = 0;

  // 检查工作区的更新时间
  if (data.workspaces) {
    data.workspaces.forEach(workspace => {
      if (workspace.updatedAt && workspace.updatedAt > maxUpdatedAt) {
        maxUpdatedAt = workspace.updatedAt;
      }
    });
  }

  // 检查待办事项的更新时间
  if (data.todos) {
    data.todos.forEach(todo => {
      const todoUpdatedAt = todo.updatedAt || todo.createdAt || 0;
      if (todoUpdatedAt > maxUpdatedAt) {
        maxUpdatedAt = todoUpdatedAt;
      }
    });
  }

  return maxUpdatedAt;
}
